import React from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { ActivityFeedItem } from '../../services/activityFeedService';
import type { ActivityFeedFilterOption } from '../../types/activityFeed';
import SelectDatePicker from '../audit/SelectDatePicker';
import { adminBorders, adminColors, adminRadii, adminTypography } from '../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminDialogActionsSx,
  adminDialogContentSx,
  adminDialogSx,
  adminDialogTitleSx,
  adminDividerSx,
  adminEmptyStateSx,
  adminFormControlSx,
} from '../../styles/adminComponentStyles';

type ActivityFeedSectionProps = {
  activityDateRange: [Date | null, Date | null];
  onActivityDateRangeChange: (range: [Date | null, Date | null]) => void;
  activityEventType: string;
  activityEventCategory: string;
  activityEventTypeOptions: ActivityFeedFilterOption[];
  activityEventCategoryOptions: ActivityFeedFilterOption[];
  onActivityEventTypeChange: (eventType: string) => void;
  onActivityEventCategoryChange: (eventCategory: string) => void;
  activityFeedLoading: boolean;
  activityFeedLoadingMore: boolean;
  activityFeedHasMore: boolean;
  activityFeed: ActivityFeedItem[];
  onActivityFeedScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  getAvatarColor: (name: string) => string;
  getInitials: (name: string) => string;
};

const renderJson = (value: Record<string, unknown> | null): string => {
  if (!value || Object.keys(value).length === 0) return '{}';
  return JSON.stringify(value, null, 2);
};

const ActivityFeedSection: React.FC<ActivityFeedSectionProps> = ({
  activityDateRange,
  onActivityDateRangeChange,
  activityEventType,
  activityEventCategory,
  activityEventTypeOptions,
  activityEventCategoryOptions,
  onActivityEventTypeChange,
  onActivityEventCategoryChange,
  activityFeedLoading,
  activityFeedLoadingMore,
  activityFeedHasMore,
  activityFeed,
  onActivityFeedScroll,
  getAvatarColor,
  getInitials,
}) => {
  const [selectedActivity, setSelectedActivity] = React.useState<ActivityFeedItem | null>(null);

  return (
    <Box>
      <Box sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', lg: 'center' },
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 1.5,
              width: '100%',
            }}
          >
            {/* <Box>
              <Typography sx={{ ...adminTypography.cardTitle, color: adminColors.text, whiteSpace: 'nowrap' }}>
                Activity Feed
              </Typography>
            </Box> */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                gap: 1.5,
                mb: 1,
                pb: 0.5,
                width: '100%',
                justifyContent: 'flex-start',
              }}
            >
              <FormControl size="small" sx={adminCombineSx(adminFormControlSx, { flex: '1 1 0', minWidth: 0 })}>
                <Select
                  displayEmpty
                  value={activityEventCategory}
                  onChange={(event: SelectChangeEvent<string>) => onActivityEventCategoryChange(event.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {activityEventCategoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={adminCombineSx(adminFormControlSx, { flex: '1 1 0', minWidth: 0 })}>
                <Select
                  displayEmpty
                  value={activityEventType}
                  onChange={(event: SelectChangeEvent<string>) => onActivityEventTypeChange(event.target.value)}
                >
                  <MenuItem value="">All Event Types</MenuItem>
                  {activityEventTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  '& > div': { width: '100% !important' },
                }}
              >
                <SelectDatePicker
                  value={activityDateRange}
                  onChange={(range) => {
                    if (range && range[0] && range[1]) {
                      onActivityDateRangeChange([range[0], range[1]]);
                    } else {
                      onActivityDateRangeChange([null, null]);
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider sx={adminDividerSx} />
      <Box>
        <Box
          sx={{
            position: 'relative',
            maxHeight: '910px',
            minHeight: '615px',
            overflowY: 'auto',
            pr: 1,
          }}
          onScroll={onActivityFeedScroll}
        >
          {!activityFeedLoading && activityFeed.length === 0 ? (
            <Box sx={adminCombineSx(adminEmptyStateSx, { my: 2, minHeight: 96 })}>
              <Typography className="AdminEmptyState-title">No Activities Found</Typography>
            </Box>
          ) : activityFeed.length > 0 ? (
            <Stack spacing={0} sx={{ opacity: activityFeedLoading ? 0.35 : 1, transition: 'opacity 0.2s ease' }}>
              {activityFeed.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <Box
                    sx={{ alignItems: 'flex-start', cursor: 'pointer', display: 'flex', gap: 1.5, py: 1.25 }}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedActivity(activity)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedActivity(activity);
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: getAvatarColor(activity.userName),
                        fontSize: 14,
                        fontWeight: 600,
                        color: adminColors.surface,
                      }}
                    >
                      {getInitials(activity.userName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ ...adminTypography.body, color: adminColors.text, letterSpacing: 0 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {activity.userName}
                        </Box>{' '}
                        -{' '}
                        <Box component="span" sx={{ color: adminColors.textSoft, fontWeight: 500 }}>
                          {activity.action}
                        </Box>
                      </Typography>
                      <Typography sx={{ ...adminTypography.helper, color: adminColors.subtleText }}>
                        {activity.time}
                      </Typography>
                    </Box>
                  </Box>
                  {index < activityFeed.length - 1 && <Divider sx={adminDividerSx} />}
                </React.Fragment>
              ))}
              {activityFeedLoadingMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!activityFeedHasMore && activityFeed.length > 0 && (
                <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, textAlign: 'center', py: 1 }}>
                  End of activity feed
                </Typography>
              )}
            </Stack>
          ) : null}

          {activityFeedLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={Boolean(selectedActivity)}
        onClose={() => setSelectedActivity(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: adminDialogSx }}
      >
        <DialogTitle sx={adminDialogTitleSx}>Activity Details</DialogTitle>
        <DialogContent dividers sx={adminDialogContentSx}>
          {selectedActivity && (
            <Stack spacing={2}>
              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>Summary</Typography>
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>{selectedActivity.summary || selectedActivity.action}</Typography>
              </Box>

              <Divider sx={adminDividerSx} />

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>Event</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>ID:</strong> {selectedActivity.id}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Store ID:</strong> {selectedActivity.storeId || '-'}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Occurred:</strong> {selectedActivity.time}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Type:</strong> {selectedActivity.eventType}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Type Label Key:</strong> {selectedActivity.eventTypeLabelKey || '-'}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Category:</strong> {selectedActivity.eventCategory}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Source:</strong> {selectedActivity.source}</Typography>
              </Box>

              <Divider sx={adminDividerSx} />

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>Actor</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Name:</strong> {selectedActivity.actorName || selectedActivity.userName}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Type:</strong> {selectedActivity.actorType}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>User ID:</strong> {selectedActivity.actorUserId || '-'}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Store User ID:</strong> {selectedActivity.actorStoreUserId || '-'}</Typography>
              </Box>

              <Divider sx={adminDividerSx} />

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>Entity</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Table:</strong> {selectedActivity.entityTable || '-'}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>ID:</strong> {selectedActivity.entityId || '-'}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Label:</strong> {selectedActivity.entityLabel || '-'}</Typography>
              </Box>

              <Divider sx={adminDividerSx} />

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>Request Tracking</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Request ID:</strong> {selectedActivity.requestId || '-'}</Typography>
                <Typography sx={{ ...adminTypography.body }}><strong>Correlation ID:</strong> {selectedActivity.correlationId || '-'}</Typography>
              </Box>

              <Divider sx={adminDividerSx} />

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>Details JSON</Typography>
                <Box
                  component="pre"
                  sx={{ backgroundColor: adminColors.surfaceAlt, border: adminBorders.surface, borderRadius: adminRadii.sm, fontSize: 12, m: 0, overflowX: 'auto', p: 1.5 }}
                >
                  {renderJson(selectedActivity.detailsJson)}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>Before JSON</Typography>
                <Box
                  component="pre"
                  sx={{ backgroundColor: adminColors.surfaceAlt, border: adminBorders.surface, borderRadius: adminRadii.sm, fontSize: 12, m: 0, overflowX: 'auto', p: 1.5 }}
                >
                  {renderJson(selectedActivity.beforeJson)}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1 }}>After JSON</Typography>
                <Box
                  component="pre"
                  sx={{ backgroundColor: adminColors.surfaceAlt, border: adminBorders.surface, borderRadius: adminRadii.sm, fontSize: 12, m: 0, overflowX: 'auto', p: 1.5 }}
                >
                  {renderJson(selectedActivity.afterJson)}
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={adminDialogActionsSx}>
          <Button
            onClick={() => setSelectedActivity(null)}
            size="small"
            variant="outlined"
            sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(ActivityFeedSection);
